<script lang="ts">
	import { useGroup } from "@components/utils/group.store";
	import Link from "@svg/Link.svelte";
	import Whatsapp from "@svg/Whatsapp.svelte";
	import { copyToClipboard } from "@utils/copyToClipboard";
	import { Dialog } from "bits-ui";
	import { onMount } from "svelte";
	import { toast } from "svelte-sonner";
	import { Drawer } from "vaul-svelte";
	import DrawerContent from "./DrawerContent.svelte";
	import DrawerHandle from "./DrawerHandle.svelte";
	import DrawerHeader from "./DrawerHeader.svelte";

	// Use a reactive variable to track screen size
	let large = $state(false);

	// Function to check if screen is large
	function checkScreenSize() {
		large = window.matchMedia("(min-width: 1024px)").matches;
	}

	// Add event listener for screen resize
	onMount(() => {
		checkScreenSize();
		window.addEventListener("resize", checkScreenSize);

		return () => {
			window.removeEventListener("resize", checkScreenSize);
		};
	});

	interface ShareData {
		link: string;
		objectID: string;
		index: number;
	}

	const { selected, setValue } = useGroup<ShareData>("socialShareDrawer");

	let drawerTriggerRef: HTMLElement | null = $state(null);
	let dialogTriggerRef: HTMLElement | null = $state(null);
	let drawerCloseRef: HTMLElement | null = $state(null);
	let dialogCloseRef: HTMLElement | null = $state(null);

	function open() {
		if (large) {
			dialogTriggerRef?.click();
		} else {
			drawerTriggerRef?.click();
		}
	}

	function close() {
		if (large) {
			dialogCloseRef?.click();
		} else {
			drawerCloseRef?.click();
		}
	}

	let shareLink = $state("");

	selected.subscribe((value) => {
		if (value) {
			setTimeout(() => {
				shareLink = window.location.origin + $selected.link;
				open();
			}, 0);
		}
	});

	async function handleCopyLink() {
		if ($selected) {
			try {
				// Use the copyToClipboard function with an explicit toast message
				await copyToClipboard(shareLink, "Link gekopieerd!");

				// Add a longer delay before closing to ensure the toast is visible
				setTimeout(() => {
					close();
				}, 1000);
			} catch (error) {
				console.error("Error copying to clipboard:", error);
				// If there's an error, show an error toast and don't close the drawer
				toast.error("Kopiëren mislukt. Probeer het opnieuw.");
			}
		}
	}
</script>

{#snippet content()}
	<DrawerContent>
		<DrawerHandle />
		<DrawerHeader
			title="Delen"
			onClose={() => {
				close();
			}}
		/>

		<ul class="space-y-4 px-2">
			<li>
				<!-- Desktop WhatsApp share link -->
				{#if $selected}
					<a
						class="hidden items-center gap-4 lg:flex"
						href="https://web.whatsapp.com/send?text=Check deze job! Iets voor jou? 👀🌰%0A{shareLink}?utm_source=whatsapp%26utm_medium=social-share"
						target="_blank"
						onclick={(event) => {
							event.stopPropagation();
							event.preventDefault();
							window.open(
								`https://web.whatsapp.com/send?text=Check deze job! Iets voor jou? 👀🌰%0A${shareLink}?utm_source=whatsapp%26utm_medium=social-share`,
								"_blank"
							);
							close();
						}}
					>
						<Whatsapp class="h-6 w-6" />
						<span>Delen via WhatsApp</span>
					</a>

					<!-- Mobile WhatsApp share link -->
					<a
						class="flex items-center gap-4 lg:hidden"
						href="whatsapp://send?text=Check deze job! Iets voor jou? 👀🌰%0A{$selected.link}?utm_source=whatsapp%26utm_medium=social-share"
						onclick={() => {
							window.open(
								`whatsapp://send?text=Check deze job! Iets voor jou? 👀🌰%0A{$selected.link}?utm_source=whatsapp%26utm_medium=social-share`,
								"_blank"
							);
							close();
						}}
					>
						<Whatsapp class="h-6 w-6" />
						<span>Delen via WhatsApp</span>
					</a>
				{/if}
			</li>
			<li>
				<!-- Copy link button with improved event handling -->
				<button
					type="button"
					class="flex w-full items-center gap-4 py-2"
					onclick={(event) => {
						// Prevent default behavior and stop event propagation
						event.stopPropagation();
						event.preventDefault();

						// Call the copy function
						handleCopyLink();

						// Return false to prevent any additional event handling
						return false;
					}}
				>
					<Link class="h-6 w-6 fill-black" />
					<span>Kopieer link</span>
				</button>
			</li>
		</ul>
	</DrawerContent>
{/snippet}

{#if !large}
	<Drawer.Root
		onClose={() => {
			setTimeout(() => {
				setValue(null);
			}, 200);
		}}
	>
		<Drawer.Trigger class="hidden">
			<div bind:this={drawerTriggerRef}></div>
		</Drawer.Trigger>
		<Drawer.Close class="hidden">
			<div bind:this={drawerCloseRef}></div>
		</Drawer.Close>

		<Drawer.Portal>
			<Drawer.Overlay class="fixed inset-0 z-[60] bg-black/80" />

			<Drawer.Content
				class="fixed bottom-0 left-0 right-0 z-[70] flex max-h-[85vh] flex-col rounded-t-[10px] bg-white"
			>
				{#if $selected}
					{@render content()}
				{/if}
			</Drawer.Content>
		</Drawer.Portal>
	</Drawer.Root>
{:else}
	<Dialog.Root
		onOpenChange={(open) => {
			if (!open) {
				setValue(null);
			}
		}}
	>
		<Dialog.Trigger class="hidden">
			<div bind:this={dialogTriggerRef}></div>
		</Dialog.Trigger>
		<Dialog.Close class="hidden" bind:ref={dialogCloseRef}></Dialog.Close>
		<Dialog.Portal>
			<Dialog.Overlay class="fixed inset-0 z-[60] bg-black/50 pl-80" />

			<Dialog.Content
				class="fixed left-[50%] top-[50%] z-[60] max-h-[85vh] w-[90vw] max-w-[800px] translate-x-[-50%] translate-y-[-50%] rounded-lg bg-white p-6 shadow-lg xl:left-[calc(50%+var(--spacing)*40)]"
			>
				{#if $selected}
					{@render content()}
				{/if}
			</Dialog.Content>
		</Dialog.Portal>
	</Dialog.Root>
{/if}
